'use client';
import React from 'react';
import { useTrackingCode } from 'react-hubspot-tracking-code-hook';

import JsonLd from '@src/components/Shared/JsonLd';
import ScrollToContainer from '@src/components/Shared/ScrollToContainer';
import Container from '@src/components/Shared/Container';
import SectionHeader from '@src/components/Shared/SectionHeader';
import RenderBodyCopy from '@src/components/Shared/RenderBodyCopy';
import ClientSuccess from '@src/components/Sections/ClientSuccess';
import NextImage from '@src/components/Shared/NextImage';
import RenderButtons from '@src/components/Shared/RenderButtons';
import SocialEmbeds from '@src/components/Shared/SocialEmbeds/SocialEmbeds';

import HeroPrimary from '@src/components/Sections/HeroPrimary';
import ImageTextPrimary from '@src/components/Sections/ImageTextPrimary';
import CallToAction from '@src/components/Sections/CallToAction';
import usePreviewContent from '@src/hooks/usePreviewContent';
import constructURL from '@src/utils/constructURL';
import ImageTextSecondary from '@src/components/Sections/ImageTextSecondary';
import Testimonials from '@src/components/Sections/Testimonials';
import ClinicalSetting from '@src/components/Sections/ClinicalSetting';

import { CliniciansClientPropTypes } from './Clinicians.types';

const CliniciansClient: React.FC<CliniciansClientPropTypes> = ({ data }) => {
  // Enable live updates for the post
  const page = usePreviewContent({ content: data?.page });

  // Set the page view path for analytics tracking
  const { setPathPageView, setContentType } = useTrackingCode();
  setPathPageView(
    constructURL({ languageCode: page?.languageCode, slug: '/clinicians', baseURL: '' })
  );
  setContentType('site-page');

  const cliniciansJsonLd = JsonLd({
    name: page?.pageName,
    pageUrl: `/${page?.languageCode?.toLowerCase()}${page?.slug === '/' ? '' : page?.slug}`,
    thumbnailUrl: page?.seoMetadata?.previewImage?.url,
    description: page?.seoMetadata?.description,
    datePublished: page?.sys?.firstPublishedAt,
    dateModified: page?.sys?.publishedAt,
    locale: page?.languageCode?.toLowerCase(),
  });

  return (
    <ScrollToContainer>
      {/* Add JSON-LD to your page */}
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(cliniciansJsonLd) }}
      />
      <div className='bg-primary'>
        <HeroPrimary
          attr='hero-component'
          id={data?.page?.hero?.items?.[0]?.scrollTo?.slice(1)}
          className='overflow-hidden pb-[0]! 2xl:h-[72rem]'
          titleClassName='text-[4rem] md:text-[6rem] lg:text-[8rem] 2xl:text-[11.2412rem] 2xl:tracking-[-0.2432rem] leading-[4rem] md:leading-[6rem] lg:leading-[8rem] 2xl:leading-[11.2412rem]  2xl:max-w-[96rem] 2xl:tracking-[-0.152rem] mb-[3.2rem] '
          imgClassName='max-w-[34rem] sm:mt-[5%] md:mt-[0] flex flex-col justify-start sm:justify-center md:block md:max-w-[50rem] 2xl:max-w-[65rem] mx-auto md:mx-[unset] md:z-2 md:bottom-[0rem] lg:bottom-[0rem] 2xl:bottom-[0] md:left-[50%] lg:left-[55%] 2xl:left-[47%] lg:min-h-[29.3891rem] sticky!'
          bodyCopyClassName='text-[1.6rem] [&>p]:text-shadow-custom md:text-[1.8rem] lg:text-[2rem] 2xl:text-[2.4rem] leading-[2.4rem] md:leading-[2.7rem] lg:leading-[3rem] 2xl:leading-[3.6rem] md:max-w-lg lg:max-w-[50rem] xl:max-w-[60rem]! 2xl:max-w-5xl'
          childrenClassName='sm:max-w-full'
          title={data?.page?.hero?.items?.[0]?.title}
          imgSrc={data?.page?.hero?.items?.[0]?.mediaPrimary?.url}
          bodyCopy={data?.page?.hero?.items?.[0]?.bodyCopy}
        />
      </div>

      <ImageTextSecondary
        className='mt-[-3rem] locale-fr:mt-[0] 2xl:mt-[-18rem]'
        id={data?.page?.clinicalSetting?.items?.[0]?.scrollTo?.slice(1)}
        title={data?.page?.clinicalSetting?.items?.[0]?.title}
        imgURL={data?.page?.clinicalSetting?.items?.[0]?.mediaPrimary?.url}
        imgAlt={data?.page?.clinicalSetting?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={data?.page?.clinicalSetting?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              items={data?.page?.clinicalSetting?.items?.[0]?.buttonCollection?.items}
            />
          </>
        }
      />

      <ClinicalSetting
        id={data?.page?.clinicalSetting?.items?.[0]?.scrollTo?.slice(1)}
        data={data?.page?.clinicalSettingCarousel?.items?.[0]}
        attr='clinicalsetting-component'
      />

      <Container className='locale-en-ca:hidden!'>
        <SectionHeader
          className='mx-auto mb-[2.4rem] md:mb-[4.1rem] 2xl:mb-[7.2rem]'
          titleClassName='text-[2.2rem] leading-[2.6rem] md:text-[2.8rem] leading-[3.0rem] md:max-w-[44rem] md:max-w-[76.5rem] xl:text-[4.8rem] xl:leading-[5.2rem]'
          title={data?.page?.valdTechnology?.items?.[0]?.title}
          align='center'
        />

        <SocialEmbeds
          className='mx-auto max-w-[103.4rem] overflow-hidden rounded-[0.6rem] shadow-[0px_5px_15px_rgba(0,0,0,0.35)]'
          data={data?.page?.valdTechnology?.items?.[0]?.extraAssetsCollection?.items?.[0]}
        />
      </Container>

      <ImageTextSecondary
        background='bg-background-tertiary'
        className='overflow-visible! py-[4rem] lg:py-[2.3rem]!'
        id={data?.page?.forClinicians?.items?.[0]?.scrollTo?.slice(1)}
        title={data?.page?.forClinicians?.items?.[0]?.title}
        imgURL={data?.page?.forClinicians?.items?.[0]?.mediaPrimary?.url}
        titleClassName='[&>span]:drop-shadow-xs'
        imgAlt={data?.page?.forClinicians?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={data?.page?.forClinicians?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <Testimonials
        id={data?.page?.testimonials?.items?.[0]?.scrollTo?.slice(1)}
        testimonials={data?.page?.testimonials?.items?.[0]?.topicCollection?.items}
      />

      <NextImage
        src={data?.page?.weExist?.items?.[0]?.mediaPrimary?.url}
        id={data?.page?.weExist?.items?.[0]?.scrollTo?.slice(1)}
        alt='A portrait'
        className='mb-[4.5rem] h-[40rem] md:h-[45rem] lg:h-[54.3rem] 2xl:mb-[4.5rem] [&>img]:object-top'
      >
        <Container className='mb-[0]! h-full'>
          <SectionHeader
            className='h-full'
            preTitleClassName='mb-[1.6rem]'
            title={data?.page?.weExist?.items?.[0]?.title}
            titleClassName='text-left md:text-center text-[2.2rem] leading-[2.6rem] tracking-[-0.0418] text-body-copy--light font-light text-[1.8rem] md:leading-[3rem] md:text-[2.8rem] lg:max-w-[90rem] 2xl:max-w-[78rem] lg:text-[4.8rem] 2xl:text-[7.2rem] lg:leading-[5rem] 2xl:leading-[7.56rem]'
            childrenClassName='relative max-w-[88.8rem]!'
            titleHighlightBold
            align='center'
          >
            <RenderBodyCopy
              className='mx-auto mt-[2.4rem] max-w-full! text-left text-[1.6rem] font-extralight text-body-copy--light md:text-center md:text-[2.4rem]  2xl:mt-[3.2rem] 2xl:max-w-[88.8rem]'
              bodyCopy={data?.page?.weExist?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons
              className='justify-left md:justify-center'
              items={data?.page?.weExist?.items?.[0]?.buttonCollection?.items}
            />
          </SectionHeader>
        </Container>
      </NextImage>

      <ClientSuccess
        data={data?.page?.clientSuccess?.items?.[0]}
        id={data?.page?.clientSuccess?.items?.[0]?.scrollTo?.slice(1)}
      />

      <ImageTextPrimary
        attr='itp-vald-hub-component'
        id={data?.page?.moreTime?.items?.[0]?.scrollTo?.slice(1)}
        reversed
        imgURL={data?.page?.moreTime?.items?.[0]?.mediaPrimary?.url}
        pretitle='VALD HUB'
        title={data?.page?.moreTime?.items?.[0]?.title}
        childrenElements={
          <>
            <RenderBodyCopy
              className='[&>p>a]:drop-shadow-xs'
              bodyCopy={data?.page?.moreTime?.items?.[0]?.bodyCopy?.json?.content}
            />
          </>
        }
      />

      <ImageTextSecondary
        id={data?.page?.tailoredCare?.items?.[0]?.scrollTo?.slice(1)}
        title={data?.page?.tailoredCare?.items?.[0]?.title}
        imgURL={data?.page?.tailoredCare?.items?.[0]?.mediaPrimary?.url}
        imgAlt={data?.page?.tailoredCare?.items?.[0]?.mediaPrimary?.description ?? ''}
        childrenElements={
          <>
            <RenderBodyCopy
              bodyCopy={data?.page?.tailoredCare?.items?.[0]?.bodyCopy?.json?.content}
            />
            <RenderButtons items={data?.page?.tailoredCare?.items?.[0]?.buttonCollection?.items} />
          </>
        }
      />

      <CallToAction
        attr='call-to-action-component'
        id={data?.page?.callToAction?.items?.[0]?.scrollTo?.slice(1)}
        title={data.page?.callToAction?.items?.[0]?.title}
        bodyCopy={data?.page?.callToAction?.items?.[0]?.bodyCopy?.json?.content}
        buttons={data?.page?.callToAction?.items?.[0]?.buttonCollection?.items}
      />
    </ScrollToContainer>
  );
};

export default CliniciansClient;
